import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Fuel, Gauge, Heart, Car, MapPin, ChevronLeft, ChevronRight, Star, CalendarCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFavorites } from "@/context/FavoritesContext";
import { StarRating } from "@/components/StarRating";

const cityOf = (loc) => {
  if (!loc) return "";
  const parts = loc.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return parts[0] || "";
  // Address shape is "...street, City, ST ZIP" (or "...street, City, ST").
  // The city is the part immediately before the state/zip segment.
  const last = parts[parts.length - 1];
  const cityIdx = /^[A-Za-z]{2}(\s+\d{5})?$/.test(last) ? parts.length - 2 : parts.length - 1;
  const candidate = parts[cityIdx] || "";
  // Don't show a street ("126 Prospect St") as if it were a city.
  return /\d/.test(candidate) ? "" : candidate;
};

export default function CarCard({ car, index = 0, query = "" }) {
  const { t } = useTranslation();
  const unavailable = car.available === false;
  const { enabled, isSaved, toggle } = useFavorites();
  const images = car.images?.length ? car.images : car.image_url ? [car.image_url] : [];
  const [idx, setIdx] = useState(0);
  const [touchX, setTouchX] = useState(null);
  const city = cityOf(car.pickup_location);
  const go = (e, dir) => { e.preventDefault(); e.stopPropagation(); setIdx((i) => (i + dir + images.length) % images.length); };
  const onTouchStart = (e) => setTouchX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX == null || images.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (Math.abs(dx) > 40) setIdx((i) => (i + (dx < 0 ? 1 : -1) + images.length) % images.length);
    setTouchX(null);
  };
  return (
    <div
      data-testid={`car-card-${car.id}`}
      className="group relative bg-white border border-slate-100 rounded-2xl shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 overflow-hidden fade-up"
      style={{ animationDelay: `${index * 80}ms`, opacity: 0 }}
    >
      {enabled && !unavailable && (
        <button
          onClick={(e) => { e.preventDefault(); toggle(car.id); }}
          data-testid={`fav-${car.id}`}
          className="absolute top-4 right-4 z-20 w-9 h-9 grid place-items-center rounded-full bg-white/90 backdrop-blur shadow hover:bg-white transition-colors"
          title={isSaved(car.id) ? t("carCard.removeSaved") : t("carCard.saveForLater")}
        >
          <Heart size={16} className={isSaved(car.id) ? "fill-rose-500 text-rose-500" : "text-slate-500"} />
        </button>
      )}
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <Link to={`/cars/${car.id}${query}`} data-testid={`car-image-link-${car.id}`} className="block w-full h-full">
          {images.length ? (
            <img src={images[idx]} alt={car.name} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${unavailable ? "grayscale opacity-60" : ""}`} />
          ) : (
            <span className="absolute inset-0 grid place-items-center text-slate-300"><Car size={40} /></span>
          )}
        </Link>
        {images.length > 1 && (
          <>
            <button onClick={(e) => go(e, -1)} data-testid={`car-photo-prev-${car.id}`} aria-label="Previous photo" className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white/85 backdrop-blur shadow text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"><ChevronLeft size={17} /></button>
            <button onClick={(e) => go(e, 1)} data-testid={`car-photo-next-${car.id}`} aria-label="Next photo" className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white/85 backdrop-blur shadow text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"><ChevronRight size={17} /></button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
              {images.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-4" : "bg-white/60"}`} />)}
            </div>
          </>
        )}
        <span className="absolute top-4 left-4 z-10 text-xs font-semibold bg-white/90 backdrop-blur text-slate-700 px-3 py-1.5 rounded-full shadow-sm">
          {car.category}
        </span>
        {unavailable && (
          <span data-testid={`car-unavailable-${car.id}`} className="absolute bottom-4 right-4 z-10 text-xs font-semibold bg-slate-900/85 text-white px-3 py-1.5 rounded-full">
            {t("carCard.unavailable")}
          </span>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-caps text-blue-600">{car.make}</p>
            <h3 className="font-heading text-xl text-slate-900 mt-1 font-semibold">{car.name}</h3>
          </div>
          <div className="text-right">
            <p className="font-heading text-2xl text-slate-900 font-bold">${Math.round(car.rates?.daily_start || car.weekly_rate / 7)}<span className="text-slate-400 text-sm font-normal">{t("carCard.perDay")}</span></p>
            <p className="text-slate-400 text-[10px] leading-tight" data-testid={`car-before-taxes-${car.id}`}>{t("carCard.beforeTaxes")}</p>
            <p className="text-slate-400 text-xs">{t("carCard.fromWeek", { rate: car.rates?.tiers?.["7"] || car.weekly_rate })}</p>
          </div>
        </div>

        {city && <p className="flex items-center gap-1.5 text-slate-500 text-sm mt-2" data-testid={`car-city-${car.id}`}><MapPin size={14} className="text-blue-600" /> {city}, MA</p>}
        {car.deposit > 0 && <p className="text-slate-500 text-sm mt-1">{t("carCard.refundableDeposit", { deposit: car.deposit })}</p>}

        <div className="flex items-center gap-x-5 gap-y-2 mt-5 text-slate-500 text-sm flex-wrap">
          <span className="flex items-center gap-2"><Users size={15} /> {car.seats}</span>
          <span className="flex items-center gap-2"><Gauge size={15} /> {car.transmission}</span>
          <span className="flex items-center gap-2"><Fuel size={15} /> {car.fuel}</span>
        </div>

        {(car.stock_number || car.vin) && (
          <div className="flex items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-400 flex-wrap" data-testid={`car-ids-${car.id}`}>
            {car.stock_number && <span>{t("carCard.stock")} <span className="font-semibold text-slate-600">#{car.stock_number}</span></span>}
            {car.vin && <span className="font-mono">VIN {car.vin}</span>}
          </div>
        )}

        {/* Host mini-profile — links to the host's inventory & reviews */}
        <Link
          to={car.host_profile_url || `/cars/${car.id}`}
          data-testid={`car-host-${car.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100 group/host"
        >
          {car.host_photo ? (
            <img src={car.host_photo} alt={car.host_name} className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0" />
          ) : (
            <span className="w-9 h-9 rounded-full bg-blue-50 border border-blue-100 grid place-items-center text-sm font-bold text-blue-700 shrink-0">
              {(car.host_name || "B").charAt(0).toUpperCase()}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-800 truncate group-hover/host:text-blue-600 transition-colors">{car.host_name || "Belo Rentals"}</p>
            <div className="flex items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 flex-wrap">
              {car.host_rating?.count > 0 ? (
                <span className="flex items-center gap-1"><StarRating value={car.host_rating.avg} size={12} /> {car.host_rating.avg.toFixed(1)} ({car.host_rating.count})</span>
              ) : (
                <span className="flex items-center gap-1 text-slate-400"><Star size={12} /> No reviews yet</span>
              )}
              <span className="flex items-center gap-1"><CalendarCheck size={12} className="text-blue-500" /> {car.trips_completed || 0} {car.trips_completed === 1 ? "trip" : "trips"}</span>
            </div>
          </div>
        </Link>

        <div className="flex gap-3 mt-6">
          <Link
            to={`/cars/${car.id}`}
            data-testid={`car-details-${car.id}`}
            className="flex-1 text-center border-2 border-slate-200 text-slate-700 py-2.5 rounded-full text-sm font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all"
          >
            {t("carCard.details")}
          </Link>
          {unavailable ? (
            <span data-testid={`car-book-disabled-${car.id}`} className="flex-1 text-center bg-slate-100 text-slate-400 py-2.5 rounded-full text-sm font-semibold cursor-not-allowed">
              {t("carCard.unavailable")}
            </span>
          ) : (
            <Link
              to={`/cars/${car.id}${query}`}
              data-testid={`car-book-${car.id}`}
              className="flex-1 text-center bg-blue-600 text-white py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              {t("carCard.book")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
